import { useRouter } from 'next/router';

/**
 * Redirection Hook
 * Motivation: Map each redirection manually to prevent Open Redirect Attacks
 * https://cheatsheetseries.owasp.org/cheatsheets/Unvalidated_Redirects_and_Forwards_Cheat_Sheet.html
 */

interface RedirectHandler {
  toRedirectKey: (path?: string) => string;
  toPathname: (redirectKey: string) => string;
}

export type RedirectKeys = 'recipes';

const REDIRECT_HANDLERS: Record<RedirectKeys, RedirectHandler> = {
  recipes: {
    toRedirectKey(path) {
      // Format: `recipes:{slug}`
      return path ? `recipes:${path}` : 'recipes';
    },
    toPathname(redirectKey) {
      // Parse `recipes:{slug}` and return `/recipes/{slug}`
      const [, slug] = decodeURIComponent(redirectKey).split(':');
      return slug ? `/recipes/${slug}` : '/recipes';
    },
  },
  // Add more to each route
};

type UseRouteRedirectionProps = {
  isAuthenticated?: boolean;
};

export function useRouteRedirection({
  isAuthenticated = false,
}: UseRouteRedirectionProps = {}) {
  const router = useRouter();

  const push = async (redirectKey?: string) => {
    await router.push({
      pathname: '/login',
      query: { redirect: redirectKey },
    });
  };

  /**
   * Redirect to the login page with a specified redirection key and optional path.
   */
  async function pushToLoginWithRedirect(
    redirectKey: RedirectKeys,
    path?: string
  ) {
    const handler = REDIRECT_HANDLERS[redirectKey];
    const redirectValue = handler.toRedirectKey(path);
    await push(redirectValue);
  }

  return {
    withAuth: async (
      cb: () => void,
      redirectKey: RedirectKeys,
      path?: string
    ) => {
      if (isAuthenticated) {
        cb();
        return;
      }
      await pushToLoginWithRedirect(redirectKey, path);
    },
    getPathnameRedirects() {
      const redirectQuery = router.query['redirect'] as string | undefined;

      if (!redirectQuery) {
        return '/';
      }

      const [key] = redirectQuery.split(':') as [RedirectKeys];
      const handler = REDIRECT_HANDLERS[key as RedirectKeys];

      return handler ? handler.toPathname(redirectQuery) : '/';
    },
  };
}
